/**
 * Invitations — the signed-in person's open invitations, as announced at
 * `users/{uid}/notifications/{token}`, and the two answers the row takes.
 * Pure state: web's invitation.sync.ts is the one writer — the listener, the
 * two callables, the hold.
 *
 * `invitationsRead` is the listener's verdict, unanswered until it first
 * answers, so no surface states an emptiness it has not read. `invitations`
 * is what the listener holds right now. `heldInvitations` is what this visit
 * answered on: an answer deletes the row server-side in the same beat it
 * settles, so a refusal would have nowhere to be said and an accepted
 * organization would vanish before it opens — a held row stands until the
 * organization opens or the identity changes. All three survive the
 * handover clear because the reconciler re-keys and resets them itself.
 *
 * `invitationAction$` / `invitationResult$` carry the two callables as
 * intents keyed by requestId — the shape `signAction$`/`signResult$` have.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import type { AcceptInvitation, Notification } from "@momo/types";
import { Subject } from "rxjs";

export type InvitationsRead = "loading" | "ready" | "error";

export const invitationsRead = state<InvitationsRead>(
	"invitations/read",
).memory("loading", { keepOnClear: true });
export const invitations = state<readonly Notification.Notification[]>(
	"invitations/open",
).memory([], { keepOnClear: true });
export const heldInvitations = state<readonly Notification.Notification[]>(
	"invitations/held",
).memory([], { keepOnClear: true });

/** What is waiting, plus what this visit answered on and still owes a word. */
export const invitationsOnScreen = computed(() => {
	const open = invitations.value;
	return [
		...open,
		...heldInvitations.value.filter(
			(held) => !open.some((row) => row.orgId === held.orgId),
		),
	];
});
export const invitationByOrganization = computed(
	() =>
		new Map(invitationsOnScreen.get().map((row) => [row.orgId, row] as const)),
);

export type InvitationAction = {
	readonly kind: "accept" | "decline";
	readonly requestId: string;
	readonly orgId: string;
	/** The operator's platform terms and privacy notice versions accepted
	 *  with a founding invitation; absent on every other answer. */
	readonly platformTerms?: AcceptInvitation.AcceptInvitationInput["platformTerms"];
};
export const invitationAction$ = new Subject<InvitationAction>();

export type InvitationResult =
	| {
			readonly requestId: string;
			readonly kind: InvitationAction["kind"];
			readonly ok: true;
	  }
	| {
			readonly requestId: string;
			readonly kind: InvitationAction["kind"];
			readonly ok: false;
			/** Absent on a failure carrying no verdict — the act did not take. */
			readonly refusal?: AcceptInvitation.InvitationRefusal;
	  };
export const invitationResult$ = new Subject<InvitationResult>();

declare module "@mhmo91/schmancy/state" {
	interface SchmancyStateRegistry {
		"invitations/read": InvitationsRead;
		"invitations/open": readonly Notification.Notification[];
		"invitations/held": readonly Notification.Notification[];
	}
}
